import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Copy, ShieldCheck, ShieldWarning } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function copy(text) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success("Copié dans le presse-papier"),
    () => toast.error("Impossible de copier")
  );
}

export default function DnsGenerator() {
  const nav = useNavigate();
  const [providers, setProviders] = useState({ mail: [], dns: [] });
  const [domain, setDomain] = useState("");
  const [mailProvider, setMailProvider] = useState("microsoft365");
  const [dnsProvider, setDnsProvider] = useState("ovh");
  const [rua, setRua] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/modules/dns-generator/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (!domain.trim()) { toast.error("Veuillez saisir un domaine"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/modules/dns-generator/generate", {
        domain: domain.trim(),
        mail_provider: mailProvider,
        dns_provider: dnsProvider,
        dmarc_rua_email: rua || null,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const Row = ({ label, value, ok }) => (
    <div className="flex items-center gap-3 p-2 rounded-md border bg-muted/30">
      {ok ? <ShieldCheck size={18} className="text-emerald-600"/> : <ShieldWarning size={18} className="text-amber-600"/>}
      <div className="text-xs mono flex-1 truncate" title={value}>{value || <span className="text-muted-foreground italic">absent</span>}</div>
    </div>
  );

  return (
    <div className="p-6 max-w-4xl">
      <button onClick={() => nav("/modules")} data-testid="dnsgen-back" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={16}/> Modules
      </button>
      <h1 className="text-3xl font-display font-bold mb-1">Générateur DNS SPF / DKIM / DMARC</h1>
      <p className="text-sm text-muted-foreground mb-6">Récupère l'état actuel et génère les enregistrements corrects prêts à copier-coller.</p>

      <div className="rounded-xl border bg-card p-5 space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Domaine</Label>
            <Input data-testid="dnsgen-domain" placeholder="exemple.fr" value={domain} onChange={(e) => setDomain(e.target.value)} disabled={busy}/>
          </div>
          <div>
            <Label>Adresse pour rapports DMARC (optionnel)</Label>
            <Input placeholder={`dmarc@${domain || "votre-domaine.fr"}`} value={rua} onChange={(e) => setRua(e.target.value)} disabled={busy}/>
          </div>
          <div>
            <Label>Fournisseur email</Label>
            <Select value={mailProvider} onValueChange={setMailProvider} disabled={busy}>
              <SelectTrigger data-testid="dnsgen-mail-provider"><SelectValue/></SelectTrigger>
              <SelectContent>
                {providers.mail.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Gestionnaire DNS</Label>
            <Select value={dnsProvider} onValueChange={setDnsProvider} disabled={busy}>
              <SelectTrigger data-testid="dnsgen-dns-provider"><SelectValue/></SelectTrigger>
              <SelectContent>
                {providers.dns.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={generate} disabled={busy} data-testid="dnsgen-run-btn" size="lg">
            {busy ? "Analyse…" : "Générer"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-5" data-testid="dnsgen-result">
          <div className="rounded-xl border bg-card p-5">
            <h2 className="font-display font-semibold mb-3">Actuellement en place sur {result.domain}</h2>
            <div className="grid grid-cols-1 gap-2">
              <Row label="SPF" value={result.current.spf?.record} ok={result.current.spf?.ok}/>
              <Row label="DMARC" value={result.current.dmarc?.record} ok={result.current.dmarc?.ok}/>
              <Row label="DKIM" value={(result.current.dkim?.records || []).length + " sélecteur(s) trouvé(s)"} ok={result.current.dkim?.ok}/>
              <Row label="MX" value={(result.current.mx?.records || []).join(" · ") || "aucun"} ok={result.current.mx?.ok}/>
            </div>
          </div>

          {["spf", "dmarc"].map((k) => {
            const p = result.proposed[k];
            return (
              <div key={k} className="rounded-xl border bg-card p-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">{k}</div>
                    <div className="text-sm">Type <b>{p.type}</b> · Hôte <b>{p.host}</b></div>
                  </div>
                  {p.already_ok && <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">✓ déjà en place</span>}
                </div>
                <div className="flex items-center gap-2 p-3 rounded-md bg-muted/50 border">
                  <div className="text-xs mono flex-1 break-all">{p.value}</div>
                  <button onClick={() => copy(p.value)} data-testid={`dnsgen-copy-${k}`} className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-foreground">
                    <Copy size={16}/>
                  </button>
                </div>
              </div>
            );
          })}

          <div className="rounded-xl border bg-card p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">MX</div>
              {result.proposed.mx.already_ok && <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">✓ MX déjà présents</span>}
            </div>
            <div className="space-y-1">
              {result.proposed.mx.records.map((r, i) => (
                <div key={i} className="flex items-center gap-2 p-3 rounded-md bg-muted/50 border">
                  <div className="text-xs mono flex-1">{r.line}</div>
                  <button onClick={() => copy(r.line)} data-testid={`dnsgen-copy-mx-${i}`} className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-foreground">
                    <Copy size={16}/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/60 p-5">
            <h3 className="font-display font-semibold text-blue-900 dark:text-blue-100 mb-2">DKIM — {result.mail_provider.label}</h3>
            <p className="text-sm text-blue-800/90 dark:text-blue-200/90">{result.proposed.dkim_instructions}</p>
          </div>

          <div className="rounded-xl border bg-card p-5">
            <h3 className="font-display font-semibold mb-2">Marche à suivre chez {result.dns_provider.toUpperCase()}</h3>
            <ol className="list-decimal list-inside space-y-1.5 text-sm">
              {result.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
